import type {
  FilterConditionCombination,
  FilterConditionExpression,
  FilterConditionExpressionText,
  FilterConditionPlugin,
} from './filter';
import type { FilterConditionDetailedExpressionText } from './filter-detailed-text';
import {
  FilterConditionAndCombination,
  FilterConditionCombinations,
  FilterConditionOrCombination,
} from './filter.combination';
import { BdEquals } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginImpl,
  FilterConditionPluginSpecification,
} from './filter.plugin';
import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BD_AND_CONDITION_ID,
  BD_MAIN_DIAGNOSIS_CONTENT_CONDITION_ID,
  BD_MAIN_DIAGNOSIS_LEFT_ANCHORED_CONTENT_CONDITION_ID,
  BD_MAIN_PROCEDURE_CONTENT_CONDITION_ID,
  BD_MAIN_PROCEDURE_LEFT_ANCHORED_CONTENT_CONDITION_ID,
  BD_OPS_CODE_CONTENT_CONDITION_ID,
  BD_OPS_CODE_LEFT_ANCHORED_CONTENT_CONDITION_ID,
  BD_OR_CONDITION_ID,
  BD_SECONDARY_CODE_CONTENT_CONDITION_ID,
  BD_SECONDARY_CODE_LEFT_ANCHORED_CONTENT_CONDITION_ID,
  BD_SECONDARY_DIAGNOSIS_CONTENT_CONDITION_ID,
  BD_SECONDARY_DIAGNOSIS_LEFT_ANCHORED_CONTENT_CONDITION_ID
} from '@bindoc-projects/condition-domain-lib';
import type { FilterRegistrationToken } from './token';
import type {
  FilterConditionCalendricAdmissionDateCondition,
  FilterConditionCalendricDismissalDateCondition,
  FilterConditionCodeSpectrumConditionJson,
} from './common-plugins';
import {
  BD_ADMISSION_DATE_CALENDRIC_CONDITION_ID,
  BD_ADMISSION_DATE_RELATIVE_CONDITION_ID,
  BD_DISMISSAL_DATE_CALENDRIC_CONDITION_ID,
  BD_DISMISSAL_DATE_RELATIVE_CONDITION_ID,
  FilterConditionAdmissionDatePluginType,
  FilterConditionCodeSpectrumPluginType,
  FilterConditionDismissalDatePluginType,
} from './common-plugins';
import type { BdIntervalRelativePart } from '@bindoc-projects/time-domain-lib';

export class FilterConditionUtil {
  public static EMPTY(): FilterConditionExpression {
    return new FilterConditionAndCombination([]);
  }

  public static equals(
    expression: FilterConditionExpression,
    otherExpression: FilterConditionExpression,
  ): boolean {
    return BdEquals.equals(expression, otherExpression, () => {
      if (expression.type !== otherExpression.type) {
        return false;
      } else {
        switch (expression.type) {
          case 'combination':
            return (expression as FilterConditionCombination).equals(
              otherExpression as FilterConditionCombination,
            );
          default:
            return (expression as FilterConditionPlugin<any>).equals(
              otherExpression as FilterConditionPlugin<any>,
            );
        }
      }
    });
  }

  public static fromConditionJson(
    jsonObj: FilterConditionDto,
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionExpression> {
    const conditionIds = Object.keys(jsonObj);
    if (conditionIds.length === 0 || conditionIds.length > 1) {
      throw new Error(
        `${FilterConditionUtil.name}.fromObject not possible for input [${JSON.stringify(jsonObj)}]`,
      );
    }

    const conditionId = conditionIds[0];
    const conditionContent = jsonObj[conditionIds[0]];
    switch (conditionId) {
      case BD_OR_CONDITION_ID:
      case BD_AND_CONDITION_ID:
        return FilterConditionCombinations.fromConditionJson(
          conditionId,
          conditionContent,
          registeredFilter,
        );
      // TODO remove all custom bindings to CODE_SPECTRUM_CONDITION_ID after migrations done BDA-4387
      case BD_SECONDARY_DIAGNOSIS_CONTENT_CONDITION_ID:
      case BD_SECONDARY_DIAGNOSIS_LEFT_ANCHORED_CONTENT_CONDITION_ID:
      case BD_MAIN_DIAGNOSIS_CONTENT_CONDITION_ID:
      case BD_MAIN_DIAGNOSIS_LEFT_ANCHORED_CONTENT_CONDITION_ID:
      case BD_MAIN_PROCEDURE_CONTENT_CONDITION_ID:
      case BD_MAIN_PROCEDURE_LEFT_ANCHORED_CONTENT_CONDITION_ID:
      case BD_OPS_CODE_CONTENT_CONDITION_ID:
      case BD_OPS_CODE_LEFT_ANCHORED_CONTENT_CONDITION_ID:
      case BD_SECONDARY_CODE_CONTENT_CONDITION_ID:
      case BD_SECONDARY_CODE_LEFT_ANCHORED_CONTENT_CONDITION_ID: {
        return Promise.resolve(
          FilterConditionCodeSpectrumPluginType.fromCondition(
            conditionContent as FilterConditionCodeSpectrumConditionJson,
          ),
        );
      }
      case BD_DISMISSAL_DATE_CALENDRIC_CONDITION_ID:
      case BD_DISMISSAL_DATE_RELATIVE_CONDITION_ID: {
        return Promise.resolve(
          FilterConditionDismissalDatePluginType.fromCondition(
            conditionContent as
              | FilterConditionCalendricDismissalDateCondition
              | {
                  start: BdIntervalRelativePart;
                  duration: BdIntervalRelativePart;
                },
          ),
        );
      }
      case BD_ADMISSION_DATE_CALENDRIC_CONDITION_ID:
      case BD_ADMISSION_DATE_RELATIVE_CONDITION_ID: {
        return Promise.resolve(
          FilterConditionAdmissionDatePluginType.fromCondition(
            conditionContent as
              | {
                  start: BdIntervalRelativePart;
                  duration: BdIntervalRelativePart;
                }
              | FilterConditionCalendricAdmissionDateCondition,
          ),
        );
      }
      default: {
        const filter = registeredFilter.find(
          (it) => it.filterConditionId === conditionId,
        );
        if (!filter) {
          return Promise.reject(
            new Error(
              `No filter plugin found for ${conditionId}, input [${JSON.stringify(jsonObj)}]`,
            ),
          );
        }
        const specification = filter.filterConditionPluginSpecification;
        // Wrapper conditions (department/deqs) carry a nested condition tree that
        // must itself be deserialized against the registrations — an async,
        // registry-driven step the synchronous `fromCondition` cannot perform.
        return specification.fromConditionAsync
          ? specification.fromConditionAsync(conditionContent, registeredFilter)
          : Promise.resolve(specification.fromCondition(conditionContent));
      }
    }
  }

  public static fromObject(
    obj: { type: string } & { [key: string]: any },
    registeredFilter: FilterRegistrationToken[],
  ): Promise<FilterConditionExpression> {
    if (!obj.type) {
      throw new Error(
        `${FilterConditionUtil.name}.fromObject not possible for input [${JSON.stringify(obj)}]`,
      );
    }

    switch (obj.type) {
      case 'combination':
        return FilterConditionCombinations.fromObject(
          obj as { type: string; kind: string; expression: Array<object> },
          registeredFilter,
        );
      default: {
        const filter = registeredFilter.find(
          (it) => it.filterType === obj.type,
        );
        return filter
          ? Promise.resolve(
              filter.filterConditionPluginSpecification.fromObject(obj),
            )
          : Promise.reject(
              new Error(
                `${FilterConditionUtil.name}.fromObject not possible for input [${JSON.stringify(obj)}]`,
              ),
            );
      }
    }
  }

  public static withoutType(
    expression: FilterConditionExpression,
    type: FilterConditionPluginSpecification<unknown>,
  ): FilterConditionExpression | null {
    switch (expression.type) {
      case 'combination': {
        const exp = expression as FilterConditionCombination;
        exp.expression = exp.expression
          .map((it) => FilterConditionUtil.withoutType(it, type))
          .filter((it) => !!it);
        return exp;
      }
      default: {
        if (expression.type === type.type) {
          return null;
        } else {
          return expression;
        }
      }
    }
  }

  public static findAll<T extends FilterConditionPluginImpl<unknown>>(
    plugin: FilterConditionPluginSpecification<unknown>,
    expression: FilterConditionExpression,
  ): T[] {
    switch (expression.type) {
      case 'combination': {
        return (expression as FilterConditionCombination).expression
          .map((childExpression) => this.findAll<T>(plugin, childExpression))
          .flat();
      }
      case plugin.type:
        return [expression as T];
      default:
        return [];
    }
  }

  public static and(
    first: FilterConditionExpression,
    second: FilterConditionExpression,
  ): FilterConditionExpression {
    if (
      first.type === 'combination' &&
      (first as FilterConditionCombination).kind === 'AND'
    ) {
      (first as FilterConditionCombination).expression.push(second);
      return first;
    } else if (
      second.type === 'combination' &&
      (second as FilterConditionCombination).kind === 'AND'
    ) {
      (second as FilterConditionCombination).expression.push(first);
      return second;
    } else {
      return new FilterConditionAndCombination([first, second]);
    }
  }

  public static or(
    first: FilterConditionExpression,
    second: FilterConditionExpression,
  ): FilterConditionExpression {
    if (
      first.type === 'combination' &&
      (first as FilterConditionCombination).kind === 'OR'
    ) {
      (first as FilterConditionCombination).expression.push(second);
      return first;
    } else if (
      second.type === 'combination' &&
      (second as FilterConditionCombination).kind === 'OR'
    ) {
      (second as FilterConditionCombination).expression.push(first);
      return second;
    } else {
      return new FilterConditionOrCombination([first, second]);
    }
  }

  public static detailedFormatter(
    expression: FilterConditionExpression,
    resolvedNames?: Map<string, string>,
  ): FilterConditionDetailedExpressionText {
    switch (expression.type) {
      case 'combination': {
        const prefix = (expression as FilterConditionCombination).kindToText();
        return (expression as FilterConditionCombination).expression.map(
          (it) => ({
            kind: prefix,
            detailedText: this.detailedFormatter(it, resolvedNames),
          }),
        );
      }
      default: {
        return (
          expression as FilterConditionPlugin<unknown>
        ).toDetailedText(resolvedNames);
      }
    }
  }

  public static formatter(
    expression: FilterConditionExpression,
  ): FilterConditionExpressionText {
    switch (expression.type) {
      case 'combination': {
        const prefix = (expression as FilterConditionCombination).kindToText();
        return (expression as FilterConditionCombination).expression.map(
          (it) => ({
            kind: prefix,
            expressionText: this.formatter(it),
          }),
        );
      }
      default: {
        return expression.toText();
      }
    }
  }
}
